import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import ArrowRightLine from '@/assets/icons/arrow_right_line.svg?component';
import { Button } from '@/components/atoms/Buttons';
import { Card, CardHeader } from '@/components/atoms/Card';
import FishHealthSummary from '@/components/atoms/FishHealthSummary/FishHealthSummary';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import { ChartHeaderActions } from '@/components/charts/actions';
import { MetricChart, useMetricChart } from '@/components/charts/generic';
import ContentBlock from '@/components/molecules/ContentBlock';
import type { DateRange } from '@/components/molecules/DateRangePicker/DateRangePicker';
import SegmentedControl from '@/components/molecules/SegmentedControl';
import type { WelfareData } from '@/domain/welfare/types';
import chartBlockStyles from '@/routes/Client/Cage/pages/Health/components/HealthDetailChartContentBlock.module.scss';
import { HEALTH_DEFAULT_DATE_RANGE_PRESET_ID } from '@/routes/Client/Cage/pages/Health/components/healthDetailChartConstants';
import { parseLocale } from '@/utils/localeFormatters';
import { createCageUrl } from '@/utils/urlGenerator';

import { buildMaturationDailySpec, buildMaturationWeeklySpec } from './useMaturationCharts';

import styles from './CageWelfareMaturation.module.scss';

const CHART_HEIGHT = 360;

export interface CageWelfareMaturationPageContentProps {
    clientId: number;
    locationId: number;
    cageId: number;
    fishHealthData: WelfareData;
    dateFrom?: string;
    dateTo?: string;
    onDateChange?: (data: DateRange) => void;
}

const DEFAULT_EQ_THRESHOLD = 0.02 / 7; //More than 2 % per week is considered a significant change

export function CageWelfareMaturationPageContent(props: CageWelfareMaturationPageContentProps) {
    const { clientId, locationId, cageId, fishHealthData, dateFrom, dateTo, onDateChange } = props;
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();

    const { lastEstimate, slopePerDay } = fishHealthData;
    const [switchValue, setSwitchValue] = useState<'weekly' | 'daily'>('weekly');

    const getStatus = (value: number) => {
        if (value > DEFAULT_EQ_THRESHOLD) {
            return 'negative';
        }
        if (value < -DEFAULT_EQ_THRESHOLD) {
            return 'positive';
        }
        return 'neutral';
    };
    const getDirection = (value: number) =>
        value > DEFAULT_EQ_THRESHOLD ? 'up' : value < -DEFAULT_EQ_THRESHOLD ? 'down' : 'flat';

    const summary = {
        maturation: {
            value: lastEstimate.matureJaw,
            slope: slopePerDay.matureJaw,
            status: getStatus(slopePerDay.matureJaw ?? 0),
            direction: getDirection(slopePerDay.matureJaw ?? 0),
        },
    } as const;

    const weeklySpec = useMemo(
        () =>
            buildMaturationWeeklySpec(
                fishHealthData,
                t('fishHealthIndicators.matureJaw'),
                i18n.language,
                t('cageWelfare.maturation.week')
            ),
        [fishHealthData, t, i18n.language]
    );
    const weeklyChart = useMetricChart(weeklySpec);

    const dailySpec = useMemo(
        () =>
            buildMaturationDailySpec(
                fishHealthData,
                {
                    matureJaw: t('fishHealthIndicators.matureJaw'),
                    axis: t('cageWelfare.maturation.maturation'),
                },
                parseLocale(i18n.language)
            ),
        [fishHealthData, t, i18n.language]
    );
    const dailyChart = useMetricChart(dailySpec);

    const weeklyChartBlock = (
        <ContentBlock
            title={t('cageWelfare.maturation.weeklyDevelopmentOfMaturation')}
            size="compact"
            spacing="none"
            className={chartBlockStyles.chartBlock}
            containerRef={weeklyChart.fullScreenContainerRef}
            headerActions={
                <ChartHeaderActions
                    chartRef={weeklyChart.chartRef}
                    chartName="welfare-maturation-weekly-chart"
                    isFullScreen={weeklyChart.isFullScreen}
                    menuDisabled={fishHealthData.weekly.length === 0}
                    onFullScreenToggle={weeklyChart.handleFullScreenToggle}
                    dateFrom={dateFrom}
                    dateTo={dateTo}
                    onDateChange={onDateChange}
                    defaultPresetId={HEALTH_DEFAULT_DATE_RANGE_PRESET_ID}
                />
            }>
            <div className={chartBlockStyles.chartBlockBody}>
                <MetricChart
                    kind="bar"
                    data={weeklyChart.data}
                    options={weeklyChart.options}
                    legendItems={weeklyChart.legendItems}
                    visibleSeries={weeklyChart.visibleSeries}
                    onToggleSeries={weeklyChart.toggleSeries}
                    chartRef={weeklyChart.chartRef}
                    height={CHART_HEIGHT}
                />
            </div>
        </ContentBlock>
    );

    const dailyChartBlock = (
        <ContentBlock
            title={t('cageWelfare.maturation.dailyDevelopmentOfMaturation')}
            size="compact"
            spacing="none"
            className={chartBlockStyles.chartBlock}
            containerRef={dailyChart.fullScreenContainerRef}
            headerActions={
                <ChartHeaderActions
                    chartRef={dailyChart.chartRef}
                    chartName="welfare-maturation-daily-chart"
                    isFullScreen={dailyChart.isFullScreen}
                    menuDisabled={fishHealthData.days.length === 0}
                    onFullScreenToggle={dailyChart.handleFullScreenToggle}
                    dateFrom={dateFrom}
                    dateTo={dateTo}
                    onDateChange={onDateChange}
                    defaultPresetId={HEALTH_DEFAULT_DATE_RANGE_PRESET_ID}
                />
            }>
            <div className={chartBlockStyles.chartBlockBody}>
                <MetricChart
                    kind="line"
                    chartRef={dailyChart.chartRef}
                    data={dailyChart.data}
                    options={dailyChart.options}
                    height={CHART_HEIGHT}
                    legendItems={dailyChart.legendItems}
                    visibleSeries={dailyChart.visibleSeries}
                    onToggleSeries={dailyChart.toggleSeries}
                />
            </div>
        </ContentBlock>
    );

    const maturationChart = switchValue === 'weekly' ? weeklyChartBlock : dailyChartBlock;

    return (
        <div className={styles.pageBoxLayout}>
            <div className={styles.leftSideBox}>
                <Card padding="lg">
                    <FishHealthSummary
                        dataRate={summary.maturation.value}
                        status={summary.maturation.status}
                        variant="inline"
                        label="maturation"
                        direction={summary.maturation.direction}
                    />
                </Card>
                <Card>
                    <div className={styles.switchWrapper}>
                        <SegmentedControl<'weekly' | 'daily'>
                            value={switchValue}
                            onChange={(value) => setSwitchValue(value)}
                            options={[
                                { value: 'weekly', label: t('cageWelfare.maturation.weekly') },
                                { value: 'daily', label: t('common.periods.daily') },
                            ]}
                        />
                    </div>
                    {maturationChart}
                </Card>
            </div>

            <Card
                padding="lg"
                header={
                    <CardHeader title={t('common.labels.detections')} level="h2" size="card" />
                }>
                <div className={styles.infoBoxContainer}>
                    <Button
                        variant={'secondary'}
                        trackingId="welfare_maturation_view_detections"
                        onClick={() =>
                            navigate(
                                `${createCageUrl(
                                    clientId,
                                    locationId,
                                    cageId
                                )}/welfare/maturation/detections`
                            )
                        }
                        iconEnd={
                            <SvgIcon
                                color="currentColor"
                                icon={ArrowRightLine}
                                size="custom"
                                customSize={20}
                            />
                        }>
                        {t('common.actions.view')}
                    </Button>
                </div>
            </Card>
        </div>
    );
}
